import { useEffect, useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { BufferOverrideDialog } from "@/components/tpr/BufferOverrideDialog/BufferOverrideDialog";
import { MakeABookingDialogProvider } from "./MakeABookingDialogProvider";
import { useMakeABookingDialog } from "./useMakeABookingDialog";
import { BookScreen } from "./screens/BookScreen";
import { RepeatSetupScreen } from "./screens/RepeatSetupScreen";
import { ConfirmationScreen } from "./screens/ConfirmationScreen";
import { DeclinedScreen } from "./screens/DeclinedScreen";
import type { MakeABookingDialogProps } from "./types";

const CurrentScreen = () => {
  const { currentScreen, isDeclined } = useMakeABookingDialog();

  // R1/R2a/R7 — a declined interceptor verdict short-circuits every screen: the member can
  // never reach the booking form, regardless of which screen the flow thinks it's on.
  if (isDeclined) {
    return <DeclinedScreen />;
  }

  switch (currentScreen) {
    case "book":
      return <BookScreen />;
    case "repeat-setup":
      return <RepeatSetupScreen />;
    case "confirmation":
      return <ConfirmationScreen />;
    default:
      return <BookScreen />;
  }
};

export const MakeABookingDialogContent = () => {
  // Task 10 — mounted alongside whichever screen is active (both BookScreen's direct "Book" CTA and
  // RepeatSetupScreen's "Book" CTA call the same submitBooking, so a single dialog instance here
  // covers both). Rendered outside the isDeclined short-circuit so it can't itself be hidden by it —
  // though in practice a declined interceptor verdict never lets the elevated user reach a submit.
  const { isSubmitting, bufferOverrideOpen, bufferOverrideMessage, confirmBufferOverride, setBufferOverrideOpen } =
    useMakeABookingDialog();

  return (
    <>
      <CurrentScreen />
      <BufferOverrideDialog
        isOpen={bufferOverrideOpen}
        onOpenChange={setBufferOverrideOpen}
        conflictMessage={bufferOverrideMessage ?? ""}
        onConfirm={() => void confirmBufferOverride()}
        isLoading={isSubmitting}
      />
    </>
  );
};

export const MakeABookingDialog = ({
  open,
  onOpenChange,
  bookingResult,
  onBookingCreated,
}: MakeABookingDialogProps) => {
  // Use a key to force provider remount when dialog opens with new booking
  const [dialogKey, setDialogKey] = useState(0);

  useEffect(() => {
    // Increment key whenever dialog opens to force fresh provider state
    if (open) {
      setDialogKey((prev) => prev + 1);
    }
  }, [open]);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        {/* DrawerTitle + DrawerDescription are required by Radix for accessibility but the
            BookScreen renders its own visible "Create a Booking" heading, so we hide these
            visually with `sr-only` to reclaim space while keeping the announcement intact. */}
        <DrawerHeader className="sr-only">
          <DrawerTitle>Create a New Booking</DrawerTitle>
          <DrawerDescription>Review the details and confirm your booking.</DrawerDescription>
        </DrawerHeader>
        <DrawerBody>
          <MakeABookingDialogProvider
            key={dialogKey}
            bookingResult={bookingResult}
            onClose={handleClose}
            onBookingCreated={onBookingCreated}
          >
            <MakeABookingDialogContent />
          </MakeABookingDialogProvider>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default MakeABookingDialog;
